/**
 * Record & Review — Review and annotations.
 *
 * The learner reads /practice/sessions/:id/review and writes annotations; a
 * coach reads /human-coaching/shared-sessions/:id/review (read-only). The
 * server decides who may see what; the client sends no identity.
 */

import { api } from '../api';
import type { PracticeReview, ReviewAnnotation } from '../../components/practiceReview/reviewTypes';

export type { PracticeReview, ReviewAnnotation } from '../../components/practiceReview/reviewTypes';

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
}

// The tag is registered here rather than in the shared tagTypes list in
// store/api.ts, so this feature does not edit that one contended line.
export const practiceReviewApi = api.enhanceEndpoints({ addTagTypes: ['PracticeReview'] }).injectEndpoints({
  endpoints: (builder) => ({
    getPracticeReview: builder.query<PracticeReview, { sessionId: string; as: 'owner' | 'coach' }>({
      query: ({ sessionId, as }) =>
        as === 'coach' ? `/human-coaching/shared-sessions/${sessionId}/review` : `/practice/sessions/${sessionId}/review`,
      transformResponse: (r: ApiEnvelope<PracticeReview>) => r.data,
      providesTags: (_r, _e, { sessionId }) => [{ type: 'PracticeReview', id: sessionId }],
    }),

    addPracticeAnnotation: builder.mutation<ReviewAnnotation, { sessionId: string; segmentIndex: number; body: string }>({
      query: ({ sessionId, segmentIndex, body }) => ({
        url: `/practice/sessions/${sessionId}/annotations`,
        method: 'POST',
        body: { segmentIndex, body },
      }),
      transformResponse: (r: ApiEnvelope<ReviewAnnotation>) => r.data,
      invalidatesTags: (_r, _e, { sessionId }) => [{ type: 'PracticeReview', id: sessionId }],
    }),

    editPracticeAnnotation: builder.mutation<ReviewAnnotation, { sessionId: string; id: string; body: string }>({
      query: ({ sessionId, id, body }) => ({
        url: `/practice/sessions/${sessionId}/annotations/${id}`,
        method: 'PATCH',
        body: { body },
      }),
      transformResponse: (r: ApiEnvelope<ReviewAnnotation>) => r.data,
      invalidatesTags: (_r, _e, { sessionId }) => [{ type: 'PracticeReview', id: sessionId }],
    }),

    deletePracticeAnnotation: builder.mutation<{ deleted: boolean }, { sessionId: string; id: string }>({
      query: ({ sessionId, id }) => ({ url: `/practice/sessions/${sessionId}/annotations/${id}`, method: 'DELETE' }),
      transformResponse: (r: ApiEnvelope<{ deleted: boolean }>) => r.data,
      invalidatesTags: (_r, _e, { sessionId }) => [{ type: 'PracticeReview', id: sessionId }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetPracticeReviewQuery,
  useAddPracticeAnnotationMutation,
  useEditPracticeAnnotationMutation,
  useDeletePracticeAnnotationMutation,
} = practiceReviewApi;
